<template>
  <div class="interactive-map">
    <div
      ref="frameRef"
      class="interactive-map__frame"
      :class="{ 'interactive-map__frame--cluster-hub-focus': syncClusterListFocus && focusPoint }"
      @pointerleave="onMapPointerLeave"
      @pointermove="onFramePointerMove"
    >
      <div class="interactive-map__stage" :style="stageStyle">
        <img
          class="interactive-map__base"
          :src="MAP_BASE"
          alt="Схема территории"
          width="1400"
          height="720"
          decoding="async"
        />
        <img
          v-for="item in zoneImgLayers"
          :key="item.id"
          class="interactive-map__layer"
          :class="{ 'interactive-map__layer--visible': isZoneActive(item.id) }"
          :src="item.layerUrl"
          alt=""
          width="1400"
          height="720"
          decoding="async"
          aria-hidden="true"
        />
        <svg class="interactive-map__svg" viewBox="0 0 1400 720" xmlns="http://www.w3.org/2000/svg">
          <g class="interactive-map__zones">
            <g
              v-for="item in zoneLayers"
              :key="`hit-${item.id}`"
              class="interactive-map__zone-group"
              :data-zone="item.id"
            >
              <polygon
                class="interactive-map__zone interactive-map__zone--hit"
                :points="item.points"
                role="button"
                :tabindex="0"
                :aria-label="item.title"
                :aria-pressed="isZoneActive(item.id)"
                @pointerenter="onZonePointerEnter(item.id, $event)"
                @pointerleave="onZonePointerLeave(item.id)"
                @click="onZoneClick(item.id, $event)"
                @keydown.enter.prevent="onZoneClick(item.id, $event)"
                @keydown.space.prevent="onZoneClick(item.id, $event)"
              />
            </g>
          </g>
        </svg>
      </div>
      <div
        v-show="showCursorLabel"
        class="interactive-map__cursor-label"
        :class="{
          'interactive-map__cursor-label--map-default': cursorLabelIsMapDefault,
        }"
        :style="cursorLabelStyle"
        role="status"
        aria-live="polite"
      >
        <span class="interactive-map__cursor-label-text">{{ cursorLabelText }}</span>
        <svg
          v-if="cursorLabelIsMapDefault"
          class="footer__metaverse-icon"
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M12.7 0.700195L6.98573 6.41448M12.7 0.700195H7.55716M12.7 0.700195V5.84305M4.34287 0.700195H4.12858C2.23503 0.700195 0.700012 2.23522 0.700012 4.12877V9.27162C0.700012 11.1652 2.23504 12.7002 4.12858 12.7002H9.27144C11.165 12.7002 12.7 11.1652 12.7 9.27163V9.05734"
            stroke="#ABABAB"
            stroke-width="1.4"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { ZONES, ZONES_IMG, getZoneCentroidById } from './utils';

const MAP_DEFAULT_CURSOR_LABEL = 'Метавселенная ИНТЦ';

const props = defineProps({
  mapHref: {
    type: String,
    default: '',
  },
  initialZoneId: {
    type: String,
    default: '',
  },
  syncClusterListFocus: {
    type: Boolean,
    default: false,
  },
});

const MAP_VIEWBOX = { w: 1400, h: 720 };
/** Масштаб карты в хабе кластеров. */
const CLUSTER_HUB_ZOOM = 1.25;
/**
 * Горизонтальная точка, куда попадает центроид выбранного кластера (доля ширины фрейма карты).
 * > 0.5 — правее геометрического центра: слева плашка со списком перекрывает часть карты.
 */
const CLUSTER_HUB_FOCUS_ANCHOR_X = 0.65;
const CLUSTER_HUB_STAGE_EASE = 'cubic-bezier(0.33, 1, 0.68, 1)';
const CLUSTER_HUB_STAGE_DURATION = '0.58s';

function getClusterHubViewport(fw) {
  if (fw <= 576) return { zoom: 2.4, anchorX: 0.5 };
  if (fw <= 992) return { zoom: 1.65, anchorX: 0.5 };
  return { zoom: CLUSTER_HUB_ZOOM, anchorX: CLUSTER_HUB_FOCUS_ANCHOR_X };
}

function viewBoxPointToFrameLocalPx(x, y, fw, fh) {
  if (fw <= 0 || fh <= 0) return null;
  const ar = MAP_VIEWBOX.w / MAP_VIEWBOX.h;
  const fr = fw / fh;
  let contentW;
  let contentH;
  let ox;
  let oy;
  if (fr > ar) {
    contentH = fh;
    contentW = fh * ar;
    ox = (fw - contentW) / 2;
    oy = 0;
  } else {
    contentW = fw;
    contentH = fw / ar;
    ox = 0;
    oy = (fh - contentH) / 2;
  }
  return {
    x: ox + (x / MAP_VIEWBOX.w) * contentW,
    y: oy + (y / MAP_VIEWBOX.h) * contentH,
  };
}

const focusTargetId = ref(props.initialZoneId?.trim() ?? '');

watch(
  () => props.initialZoneId,
  (v) => {
    focusTargetId.value = v?.trim() ?? '';
  },
);

const focusPoint = computed(() => {
  const id = focusTargetId.value?.trim();
  if (!id || !props.syncClusterListFocus) return null;
  return getZoneCentroidById(id);
});

const MAP_IMAGES_BASE =
  process.env.NODE_ENV === 'development'
    ? '/assets/images/map'
    : '/local/templates/dist/assets/images/map';

const MAP_BASE = `${MAP_IMAGES_BASE}/map.png`;

const frameRef = ref(null);
const pointerX = ref(0);
const pointerY = ref(0);

const zoneImgLayers = ZONES_IMG.map((z) => ({
  ...z,
  layerUrl: `${MAP_IMAGES_BASE}/layer_${z.layerId ?? z.id}.png`,
}));

const zoneLayers = ZONES.map((z) => ({
  ...z,
  layerUrl: `${MAP_IMAGES_BASE}/layer_${z.layerId ?? z.id}.png`,
}));

const hoveredId = ref(null);
const outlineHovered = ref(false);

const focusPinned = computed(() => props.syncClusterListFocus && Boolean(focusPoint.value));

const showCursorLabel = computed(() =>
  Boolean(
    hoveredId.value || outlineHovered.value || (focusPinned.value && focusTargetId.value?.trim()),
  ),
);

const labelZoneId = computed(() => {
  if (hoveredId.value) return hoveredId.value;
  if (focusPinned.value) return focusTargetId.value?.trim() ?? '';
  return '';
});

const activeId = computed(() => hoveredId.value || (focusPinned.value ? labelZoneId.value : ''));

const clusterTitleById = computed(() => {
  const id = labelZoneId.value;
  if (!id) return '';
  return ZONES.find((z) => z.id === id)?.title ?? '';
});

const cursorLabelText = computed(() =>
  labelZoneId.value ? clusterTitleById.value : MAP_DEFAULT_CURSOR_LABEL,
);

const cursorLabelIsMapDefault = computed(() => !labelZoneId.value);

const frameMetrics = ref({ w: 0, h: 0 });
let frameResizeObserver = null;

function measureFrameMetrics() {
  const el = frameRef.value;
  if (!el) return;
  const { width, height } = el.getBoundingClientRect();
  frameMetrics.value = { w: width, h: height };
}

const stageStyle = computed(() => {
  if (!focusPoint.value) return {};
  const t = `${CLUSTER_HUB_STAGE_DURATION} ${CLUSTER_HUB_STAGE_EASE}`;
  const fw = frameMetrics.value.w;
  const fh = frameMetrics.value.h;
  const { zoom: S, anchorX } = getClusterHubViewport(fw);
  if (fw > 0 && fh > 0) {
    const pos = viewBoxPointToFrameLocalPx(focusPoint.value.x, focusPoint.value.y, fw, fh);
    if (pos) {
      const { x: cx, y: cy } = pos;
      const ax = fw * anchorX;
      const ay = fh / 2;
      return {
        transformOrigin: '0 0',
        transform: `translate(${ax}px, ${ay}px) scale(${S}) translate(${-cx}px, ${-cy}px)`,
        transition: `transform ${t}`,
      };
    }
  }
  const { x, y } = focusPoint.value;
  const ox = (x / MAP_VIEWBOX.w) * 100;
  const oy = (y / MAP_VIEWBOX.h) * 100;
  return {
    transformOrigin: `${ox}% ${oy}%`,
    transform: `scale(${S})`,
    transition: `transform ${t}, transform-origin ${t}`,
  };
});

const pinnedLabelPos = computed(() => {
  if (!focusPinned.value || !focusPoint.value) return null;
  const { w: fw, h: fh } = frameMetrics.value;
  if (fw > 0 && fh > 0) {
    const { anchorX } = getClusterHubViewport(fw);
    return {
      x: fw * anchorX,
      y: fh / 2,
    };
  }
  return viewBoxPointToFrameLocalPx(focusPoint.value.x, focusPoint.value.y, fw, fh);
});

const cursorLabelStyle = computed(() => {
  if (hoveredId.value) {
    return {
      left: `${pointerX.value}px`,
      top: `${pointerY.value}px`,
    };
  }
  if (focusPinned.value && pinnedLabelPos.value) {
    const { x, y } = pinnedLabelPos.value;
    return {
      left: `${x}px`,
      top: `${y}px`,
    };
  }
  return {
    left: `${pointerX.value}px`,
    top: `${pointerY.value}px`,
  };
});

function isZoneActive(id) {
  return activeId.value === id;
}

function updatePointerPosition(event) {
  const el = frameRef.value;
  if (!el) return;
  const rect = el.getBoundingClientRect();
  pointerX.value = event.clientX - rect.left;
  pointerY.value = event.clientY - rect.top;
}

function onZonePointerEnter(id, event) {
  hoveredId.value = id;
  updatePointerPosition(event);
}

function onFramePointerMove(event) {
  if (!hoveredId.value && !outlineHovered.value) return;
  updatePointerPosition(event);
}

function onZonePointerLeave(id) {
  if (hoveredId.value === id) {
    hoveredId.value = null;
  }
}

function onMapPointerLeave() {
  hoveredId.value = null;
  outlineHovered.value = false;
}

function openMapLink(url) {
  window.open(url, '_blank', 'noopener,noreferrer');
}

function onZoneClick(id, event) {
  const zone = ZONES.find((z) => z.id === id);
  if (zone?.href) {
    event?.stopPropagation?.();
    if (props.syncClusterListFocus) {
      window.dispatchEvent(
        new CustomEvent('stc:cluster-zone-select', {
          detail: { zoneId: id },
        }),
      );
      return;
    }
    openMapLink(zone.href);
    return;
  }
  onMapOutlineClick(event);
}

function onMapOutlineClick(event) {
  event?.stopPropagation?.();
  openMapLink('https://my.sticmsu.cubemeta.ru/');
}

function onClusterZoneSelect(event) {
  const zoneId = event?.detail?.zoneId?.trim();
  if (!zoneId) return;
  focusTargetId.value = zoneId;
}

onMounted(() => {
  if (props.syncClusterListFocus) {
    measureFrameMetrics();
    const el = frameRef.value;
    if (el && typeof ResizeObserver !== 'undefined') {
      frameResizeObserver = new ResizeObserver(() => measureFrameMetrics());
      frameResizeObserver.observe(el);
    }
    window.addEventListener('resize', measureFrameMetrics);
    window.addEventListener('stc:cluster-zone-select', onClusterZoneSelect);
  }
});

watch([focusTargetId, focusPinned], () => {
  if (!props.syncClusterListFocus) return;
  requestAnimationFrame(() => measureFrameMetrics());
});

onBeforeUnmount(() => {
  if (props.syncClusterListFocus) {
    window.removeEventListener('resize', measureFrameMetrics);
    window.removeEventListener('stc:cluster-zone-select', onClusterZoneSelect);
    if (frameResizeObserver) {
      frameResizeObserver.disconnect();
      frameResizeObserver = null;
    }
  }
});
</script>

<style scoped>
.interactive-map {
  width: 100%;
  max-width: 100%;
}

.interactive-map__frame {
  position: relative;
  width: 100%;
}

.interactive-map__frame--cluster-hub-focus {
  overflow: hidden;
}

.interactive-map__stage {
  position: relative;
  width: 100%;
  display: block;
  transform-origin: 50% 50%;
  will-change: transform;
}

.interactive-map__cursor-label {
  position: absolute;
  z-index: 10;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: min(260px, calc(100% - 16px));
  padding: 8px 14px;
  border-radius: 8px;
  background: #fff;
  color: var(--stc-black);
  font-family: var(--stc-font-medium), sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.3;
  pointer-events: none;
  box-shadow: 0 4px 14px rgba(12, 0, 84, 0.25);
  border: 1px solid var(--stc-gray-300);
  transform: translate(5px, calc(-100%));
  white-space: nowrap;
}

.interactive-map__cursor-label::before,
.interactive-map__cursor-label::after {
  content: '';
  position: absolute;
  width: 0;
  height: 0;
  border-style: solid;
  border-left-color: transparent;
  border-right-color: transparent;
  border-bottom-width: 0;
  pointer-events: none;
}

/* Хвостик: внешний треугольник = продолжение border 1px; чуть заходим на карточку, иначе видна щель у скругления */
.interactive-map__cursor-label::before {
  top: calc(100% - 0px);
  left: 8px;
  border-width: 7px 6px 0 6px;
  border-top-color: var(--stc-gray-300);
}

/* Внутренний на 1px меньше и на 1px выше — заполняет «обводку», концы совпадают с ::before */
.interactive-map__cursor-label::after {
  top: calc(100% - 2px);
  left: 9px;
  border-width: 7px 7px 0 5px;
  border-top-color: #fff;
}

.interactive-map__cursor-label-text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.footer__metaverse-icon {
  flex-shrink: 0;
  display: block;
}

.interactive-map__cursor-label--map-default {
  background: #fff;
  color: var(--stc-black);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.interactive-map__cursor-label--map-default::before {
  border-top-color: rgba(0, 0, 0, 0.06);
}

.interactive-map__base {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.interactive-map__layer {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.interactive-map__layer--visible {
  opacity: 1;
}

.interactive-map__svg {
  position: absolute;
  left: -3%;
  top: -6%;
  width: 100%;
  height: 100%;
  z-index: 1;
  pointer-events: none;
  transform: translate(35px, 30px);
  & > :not(svg) {
    transform-origin: 0px 0px;
  }
}

.interactive-map__map-outline {
  fill: transparent;
  pointer-events: fill;
  stroke: none;
  cursor: pointer;
}

.interactive-map__map-outline--clickable {
  cursor: pointer;
}

.interactive-map__zone-group {
  cursor: pointer;
  opacity: 0;
  pointer-events: auto;
}
</style>
